import { Flame, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";
import type { Moment, MomentCategoryNode, MomentStats } from "../../types";
import { MomentIcon } from "../../lib/momentIcon";
import { todayLocalDay } from "../../lib/moments";

type Props = {
  moments: Moment[];
  categories: MomentCategoryNode[];
  stats: MomentStats;
  filterCategoryId: string | null;
  onFilter: (id: string | null) => void;
  onDelete: (id: string) => void;
};

function flattenSelectable(nodes: MomentCategoryNode[]): MomentCategoryNode[] {
  const out: MomentCategoryNode[] = [];
  const walk = (list: MomentCategoryNode[]) => {
    list.forEach((node) => {
      if (node.parentId) {
        out.push(node);
      }
      walk(node.children);
    });
  };
  walk(nodes);
  return out;
}

function relativeDayLabel(localDay: string, today: string): string {
  if (localDay === today) {
    return "Hôm nay";
  }
  const [y, m, d] = today.split("-").map(Number);
  const yesterday = new Date(Date.UTC(y, m - 1, d));
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  const pad = (n: number) => String(n).padStart(2, "0");
  const yStr = `${yesterday.getUTCFullYear()}-${pad(yesterday.getUTCMonth() + 1)}-${pad(yesterday.getUTCDate())}`;
  if (localDay === yStr) {
    return "Hôm qua";
  }
  const [yy, mm, dd] = localDay.split("-");
  return `${dd}/${mm}/${yy}`;
}

function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" });
}

export function MomentsStatsView({ moments, categories, stats, filterCategoryId, onFilter, onDelete }: Props) {
  const [lightbox, setLightbox] = useState<string | null>(null);
  const today = todayLocalDay();
  const selectable = useMemo(() => flattenSelectable(categories), [categories]);

  const filtered = filterCategoryId ? moments.filter((moment) => moment.categoryId === filterCategoryId) : moments;
  const byDay = useMemo(() => {
    const map = new Map<string, Moment[]>();
    filtered.forEach((moment) => {
      const list = map.get(moment.localDay) ?? [];
      list.push(moment);
      map.set(moment.localDay, list);
    });
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [filtered]);

  const { done, total } = stats.routineCompletion;
  const routinePct = total > 0 ? Math.round((done / total) * 100) : 0;
  const maxPerCat = Math.max(1, ...stats.perCategory.map((item) => item.count));

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-line bg-white p-4 shadow-panel">
          <p className="text-sm font-bold text-ink">Nếp sinh hoạt hôm nay</p>
          <div className="mt-2 flex items-center justify-between text-sm text-muted">
            <span>
              {done}/{total} mục
            </span>
            <span className="font-semibold text-ink">{routinePct}%</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-leaf" style={{ width: `${routinePct}%` }} />
          </div>
        </div>

        <div className="rounded-lg border border-line bg-white p-4 shadow-panel">
          <p className="text-sm font-bold text-ink">Chuỗi thói quen</p>
          {stats.habitStreaks.length === 0 ? (
            <p className="mt-2 text-sm text-muted">Chưa có thói quen nào được theo dõi.</p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-2">
              {stats.habitStreaks.map((habit) => (
                <span
                  key={habit.categoryId}
                  className="inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-700"
                >
                  <Flame size={13} aria-hidden="true" />
                  {habit.name}
                  <span className="tabular-nums">{habit.streak} ngày</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Per-category bars */}
      {stats.perCategory.some((item) => item.count > 0) ? (
        <div className="rounded-lg border border-line bg-white p-4 shadow-panel">
          <p className="mb-3 text-sm font-bold text-ink">Số lần theo nhóm</p>
          <div className="space-y-2">
            {stats.perCategory
              .filter((item) => item.count > 0)
              .map((item) => (
                <div key={item.id} className="flex items-center gap-3 text-sm">
                  <span className="w-28 shrink-0 truncate text-muted">{item.name}</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={`h-full rounded-full ${item.kind === "habit" ? "bg-focus" : "bg-brand"}`}
                      style={{ width: `${Math.round((item.count / maxPerCat) * 100)}%` }}
                    />
                  </div>
                  <span className="w-8 text-right font-semibold tabular-nums text-ink">{item.count}</span>
                </div>
              ))}
          </div>
        </div>
      ) : null}

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onFilter(null)}
          className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${
            filterCategoryId === null ? "border-brand bg-brand text-white" : "border-line bg-white text-ink hover:bg-slate-50"
          }`}
        >
          Tất cả
        </button>
        {selectable.map((category) => (
          <button
            key={category.id}
            type="button"
            onClick={() => onFilter(category.id)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold ${
              filterCategoryId === category.id
                ? "border-brand bg-brand text-white"
                : "border-line bg-white text-ink hover:bg-slate-50"
            }`}
          >
            <MomentIcon
              name={category.icon}
              size={14}
              className={filterCategoryId === category.id ? "text-white" : "text-muted"}
            />
            {category.name}
          </button>
        ))}
      </div>

      {/* Timeline */}
      {byDay.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line bg-white p-8 text-center text-sm text-muted">
          Chưa có khoảnh khắc nào. Mở trên điện thoại để chụp nhé.
        </div>
      ) : (
        <div className="space-y-4">
          {byDay.map(([day, list]) => (
            <div key={day} className="rounded-lg border border-line bg-white p-4 shadow-panel">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-bold text-ink">{relativeDayLabel(day, today)}</p>
                <span className="text-xs font-medium text-muted">{list.length} khoảnh khắc</span>
              </div>
              <ul className="space-y-3">
                {list.map((moment) => (
                  <li key={moment.id} className="flex gap-3">
                    {moment.photoUrl ? (
                      <button
                        type="button"
                        onClick={() => setLightbox(moment.photoUrl)}
                        className="shrink-0 overflow-hidden rounded-lg border border-line"
                      >
                        <img
                          src={moment.photoUrl}
                          alt={moment.category?.name ?? "Khoảnh khắc"}
                          className="h-16 w-16 object-cover"
                          loading="lazy"
                        />
                      </button>
                    ) : (
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border border-line bg-slate-50 text-slate-300">
                        <MomentIcon name={moment.category?.icon} size={20} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {moment.category ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-line bg-slate-50 px-2 py-0.5 text-xs font-semibold text-ink">
                            <MomentIcon name={moment.category.icon} size={12} className="text-muted" />
                            {moment.category.name}
                          </span>
                        ) : (
                          <span className="text-xs font-semibold text-slate-400">Chưa phân loại</span>
                        )}
                        <span className="text-xs text-muted">{formatTime(moment.loggedAt)}</span>
                      </div>
                      {moment.note ? <p className="mt-1 text-sm text-slate-600">{moment.note}</p> : null}
                    </div>
                    <button
                      type="button"
                      aria-label="Xoá khoảnh khắc"
                      onClick={() => onDelete(moment.id)}
                      className="h-8 w-8 shrink-0 rounded-lg text-slate-300 hover:bg-rose-50 hover:text-rose-600"
                    >
                      <Trash2 size={15} aria-hidden="true" className="mx-auto" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox ? (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-6"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            aria-label="Đóng"
            className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white"
            onClick={() => setLightbox(null)}
          >
            <X size={20} aria-hidden="true" />
          </button>
          <img src={lightbox} alt="Khoảnh khắc" className="max-h-[90dvh] max-w-full rounded-lg object-contain" />
        </div>
      ) : null}
    </div>
  );
}
